import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TOMMY_SPLASH = "https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/8a5b3d0ce503ee6e5d109b6acca877668382c804aa320000e401fda7fbbae6b4.png";

export function SplashScreen({ onComplete }) {
  const [phase, setPhase] = useState(0); // 0: logo, 1: tagline, 2: exit

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 800),
      setTimeout(() => setPhase(2), 2200),
      setTimeout(() => onComplete(), 3000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {phase < 2 && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.1 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-[9999] bg-[#030303] flex items-center justify-center overflow-hidden"
          data-testid="splash-screen"
        >
          {/* Animated background particles - Teal/Green/Blue */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  background: i % 3 === 0 ? '#14b8a6' : i % 3 === 1 ? '#22c55e' : '#2563eb',
                }}
                initial={{
                  x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
                  y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
                  scale: 0,
                }}
                animate={{
                  y: [null, Math.random() * -500],
                  scale: [0, 1, 0],
                  opacity: [0, 0.8, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: 'easeOut',
                }}
              />
            ))}
          </div>

          {/* Teal circuit lines */}
          <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="tealGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14b8a6" stopOpacity="0" />
                <stop offset="50%" stopColor="#14b8a6" stopOpacity="1" />
                <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <motion.path
              d="M0,200 Q400,100 800,200 T1600,200"
              stroke="url(#tealGradient)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, ease: 'easeInOut' }}
            />
            <motion.path
              d="M0,400 Q400,300 800,400 T1600,400"
              stroke="url(#tealGradient)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: 0.3, ease: 'easeInOut' }}
            />
          </svg>

          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Tommy mascot with teal glow */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 200,
                damping: 20,
                duration: 0.8,
              }}
              className="relative mb-8"
            >
              {/* Outer glow rings - Teal */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(20,184,166,0.4) 0%, transparent 70%)',
                  filter: 'blur(30px)',
                }}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              {/* Inner glow */}
              <motion.div
                className="absolute inset-4 rounded-full bg-teal-500/20 blur-xl"
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />

              {/* Tommy image */}
              <motion.img
                src={TOMMY_SPLASH}
                alt="Tommy The Tuxedo Cat"
                className="w-48 h-48 md:w-64 md:h-64 rounded-full object-cover relative z-10 border-4 border-teal-500/50"
                style={{
                  boxShadow: '0 0 60px rgba(20, 184, 166, 0.5), 0 0 120px rgba(20, 184, 166, 0.3)',
                }}
                animate={{
                  boxShadow: [
                    '0 0 60px rgba(20, 184, 166, 0.5), 0 0 120px rgba(20, 184, 166, 0.3)',
                    '0 0 80px rgba(20, 184, 166, 0.7), 0 0 150px rgba(20, 184, 166, 0.4)',
                    '0 0 60px rgba(20, 184, 166, 0.5), 0 0 120px rgba(20, 184, 166, 0.3)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.div>

            {/* Logo text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-7xl font-black tracking-tight">
                <span className="text-white">Triple</span>
                <span className="bg-gradient-to-r from-teal-400 via-green-400 to-emerald-400 bg-clip-text text-transparent">TC</span>
              </h1>
            </motion.div>

            {/* Tagline */}
            <AnimatePresence>
              {phase >= 1 && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-4 text-lg md:text-xl text-zinc-400 tracking-widest uppercase"
                >
                  <span className="inline-block">
                    {'The Future of AI Work'.split('').map((char, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                      >
                        {char}
                      </motion.span>
                    ))}
                  </span>
                </motion.p>
              )}
            </AnimatePresence>

            {/* Loading bar - Teal gradient */}
            <motion.div
              className="mt-8 w-48 h-1 bg-zinc-800 rounded-full overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <motion.div
                className="h-full bg-gradient-to-r from-teal-500 via-green-400 to-teal-500 rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 2.2, ease: 'easeInOut' }}
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SplashScreen;
